import { Trans } from '@lingui/react';
import { Card } from '@startuptools/ui/Card';
import { Stack } from '@startuptools/ui/Stack';
import { Table } from '@startuptools/ui/Table';
import { Typography } from '@startuptools/ui/Typography';
import { ButtonGroup } from '@startuptools/ui/ButtonGroup';
import { IconButton } from '@startuptools/ui/IconButton';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import { GqlCompanyInviteRequest } from '../../../graphql/base-types.graphql';
import { captureException } from '@sentry/react';
import { toast } from '@startuptools/ui/toast/use-toast';
import {
  useAcceptCompanyInviteRequestMutation,
  useRejectCompanyInviteRequestMutation,
} from './UserInviteRequestsTable.graphql';
import { SettingsUsersDocument } from './page.graphql';
import { Tooltip } from '@startuptools/ui/Tooltip';

export const UserInvitesRequestsTable = ({
  invites,
}: {
  invites: Pick<GqlCompanyInviteRequest, 'id' | 'role' | 'userName' | 'userEmail' | 'acceptedAt' | 'rejectedAt'>[];
}) => {
  const companyId = useCompanyId();
  // eslint-disable-next-line sut/loading-mutation
  const [doReject] = useRejectCompanyInviteRequestMutation({ refetchQueries: [SettingsUsersDocument] });
  // eslint-disable-next-line sut/loading-mutation
  const [doAccept] = useAcceptCompanyInviteRequestMutation({ refetchQueries: [SettingsUsersDocument] });

  const handleReject = async (invite: Pick<GqlCompanyInviteRequest, 'id'>) => {
    try {
      await doReject({ variables: { companyId, id: invite.id } });
      toast({
        title: <Trans id="Success" />,
        severity: 'success',
        description: <Trans id="The invite request was successfully rejected" />,
      });
    } catch (e) {
      captureException(e);
      toast({
        title: <Trans id="Error!" />,
        description: (
          <Trans id="An error occurred while rejecting the invite request. Try it again and if the error persists, contact our customer support." />
        ),
        severity: 'danger',
      });
    }
  };
  const handleAccept = async (invite: Pick<GqlCompanyInviteRequest, 'id'>) => {
    try {
      await doAccept({ variables: { companyId, id: invite.id } });
      toast({
        title: <Trans id="Success" />,
        severity: 'success',
        description: <Trans id="The invite request was successfully accepted and the user was added to the company" />,
      });
    } catch (e) {
      captureException(e);
      toast({
        title: <Trans id="Error!" />,
        description: (
          <Trans id="An error occurred while rejecting the invite request. Try it again and if the error persists, contact our customer support." />
        ),
        severity: 'danger',
      });
    }
  };

  return (
    <Stack gap={2} mt={3}>
      <Typography level="h4">
        <Trans id="Requests" />
      </Typography>
      <Card>
        <Table sx={{ tableLayout: 'auto' }}>
          <thead>
            <tr>
              <th>
                <Trans id="Email" />
              </th>
              <th>
                <Trans id="Name" />
              </th>
              <th>
                <Trans id="Role" />
              </th>
              <th align="right" />
            </tr>
          </thead>
          <tbody>
            {invites.map(invite => (
              <tr key={invite.id}>
                <td>{invite.userEmail}</td>
                <td>{invite.userName}</td>
                <td>{invite.role}</td>
                <td align="right">
                  <Stack direction="row" justifyContent="end">
                    <ButtonGroup>
                      <Tooltip title={<Trans id="Accept invite request and add user" />}>
                        <IconButton color="primary" onClick={() => handleAccept(invite)}>
                          <CheckIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={<Trans id="Reject invite request" />}>
                        <IconButton color="danger" onClick={() => handleReject(invite)}>
                          <CloseIcon />
                        </IconButton>
                      </Tooltip>
                    </ButtonGroup>
                  </Stack>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card>
    </Stack>
  );
};
